import { useState } from "react";
import { Link2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Spinner } from "@/components/ui/spinner";
import { useAssignDepartmentBulk } from "@/features/content/hooks/useContentMutations";
import {
  DEPARTMENT_PAGES_CONTENT_TYPE,
  useDepartmentPages,
} from "@/features/departments/hooks/useDepartmentPages";

interface AssignPagesDialogProps {
  departmentId: string;
}

export function AssignPagesDialog({ departmentId }: AssignPagesDialogProps) {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const { data, isLoading } = useDepartmentPages({
    unassigned: true,
    pageSize: 50,
    enabled: open,
  });
  const assignBulk = useAssignDepartmentBulk();

  const pages = data?.items ?? [];

  const togglePage = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleAssign = async () => {
    if (selected.size === 0) return;
    await assignBulk.mutateAsync({
      contentType: DEPARTMENT_PAGES_CONTENT_TYPE,
      ids: Array.from(selected),
      departmentId,
    });
    setSelected(new Set());
    setOpen(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setSelected(new Set());
      }}
    >
      <DialogTrigger asChild>
        <Button type="button" variant="outline" size="sm">
          <Link2 className="size-4" />
          ربط صفحات
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[85vh] flex flex-col">
        <DialogHeader className="text-right">
          <DialogTitle>ربط صفحات بالدائرة</DialogTitle>
          <DialogDescription>
            اختر الصفحات غير المرتبطة بدائرة لربطها بهذه الدائرة.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto min-h-0 space-y-2 py-2">
          {isLoading ? (
            <div className="flex justify-center py-8">
              <Spinner className="size-6" />
            </div>
          ) : pages.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">
              لا توجد صفحات غير مرتبطة بدائرة.
            </p>
          ) : (
            pages.map((page) => {
              const id = String(page.id);
              return (
                <label
                  key={id}
                  className="flex items-center justify-between gap-3 rounded-lg border p-3 cursor-pointer hover:bg-muted/50"
                >
                  <Checkbox
                    checked={selected.has(id)}
                    onCheckedChange={() => togglePage(id)}
                  />
                  <div className="flex-1 text-right min-w-0">
                    <div className="font-medium text-sm truncate">
                      {page.title ?? page.slug ?? id}
                    </div>
                    {page.slug && (
                      <div className="text-xs text-muted-foreground font-mono truncate">
                        {page.slug}
                      </div>
                    )}
                  </div>
                </label>
              );
            })
          )}
        </div>

        <DialogFooter className="gap-2 sm:justify-start">
          <Button
            type="button"
            onClick={handleAssign}
            disabled={selected.size === 0 || assignBulk.isPending}
          >
            {assignBulk.isPending
              ? "جاري الربط..."
              : `ربط ${selected.size > 0 ? `(${selected.size})` : ""}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
